import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CrewCertificate, Employee, Car } from '../../types';
import Button from '../Form/Button';
import { useEmployees } from '../../hooks/useEmployees';
import { useCars } from '../../hooks/useCars';
import { useRanks } from '../../hooks/useRanks';
import { useDocumentTypes } from '../../hooks/useDocumentTypes';
import {
  DocumentCategorySelector,
  DocumentFormFields,
  DocumentFileUpload,
} from './CrewAndCarsDocumentSections';

interface CrewCertificateFormData {
  documentCategory: 'employee' | 'car';
  employeeId: string;
  carId: string;
  courseType: string;
  selectedSeamanDocument?: string;
  profession: string;
  issueDate: string;
  expiryDate: string;
  certificateNumber: string;
  capacity: string;
  limitation: string;
  certificateFile?: FileList;
}

interface CrewAndCarsDocumentFormProps {
  certificate?: CrewCertificate;
  onSubmit: (data: Partial<CrewCertificate>) => void;
  onCancel: () => void;
}

const employeeDocumentTypes = [
  'STCW Basic Safety Training',
  'Advanced Fire Fighting',
  'Personal Survival Techniques',
  'Elementary First Aid',
  'Personal Safety and Social Responsibilities',
  'Security Awareness',
  'Designated Security Duties',
  'Ship Security Officer',
  'Bridge Resource Management',
  'Engine Resource Management',
  'Medical First Aid',
  'Medical Care',
  'Radar Observer',
  'ARPA',
  'GMDSS General Operator',
  'GMDSS Restricted Operator',
  'National ID (البطاقة الشخصية)',
  'Passport (جواز السفر)',
  "Seaman's Book (الجواز البحري)",
  'Medical Certificate (شهادة طبية)',
  'Other (أخرى)'
];

const carDocumentTypes = [
  'Vehicle License (رخصة السيارة)',
  'Registration (التسجيل)',
  'Insurance (التأمين)',
  'Inspection Certificate (شهادة الفحص)',
  'Ownership Document (وثيقة الملكية)',
  'Other (أخرى)'
];

const CrewAndCarsDocumentForm: React.FC<CrewAndCarsDocumentFormProps> = ({ 
  certificate, 
  onSubmit, 
  onCancel 
}) => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiCars = [] } = useCars({ populate: '*' });
  const { data: ranksData = [] } = useRanks({ pagination: { pageSize: 100 } });
  const { data: documentTypes = [] } = useDocumentTypes({ pagination: { pageSize: 100 } });
  const employees = apiEmployees as Employee[];
  const cars = apiCars as Car[];
  const [documentCategory, setDocumentCategory] = useState<'employee' | 'car'>('employee');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<CrewCertificateFormData>({
    defaultValues: certificate ? {
      documentCategory: certificate.carId ? 'car' : 'employee',
      employeeId: certificate.employeeId,
      carId: certificate.carId || '',
      courseType: certificate.courseType,
      profession: certificate.profession,
      issueDate: certificate.issueDate,
      expiryDate: certificate.expiryDate,
      certificateNumber: certificate.certificateNumber,
      capacity: certificate.capacity,
      limitation: certificate.limitation
    } : {
      documentCategory: 'employee',
      employeeId: '', carId: '', courseType: '', profession: '',
      issueDate: '', expiryDate: '', certificateNumber: '',
      capacity: '', limitation: ''
    }
  });

  const watchedEmployeeId = watch('employeeId');
  const watchedCarId = watch('carId');

  useEffect(() => {
    if (certificate) reset(certificate);
  }, [certificate, reset]);

  useEffect(() => {
    if (watchedEmployeeId) {
      setSelectedEmployee(employees.find(e => e.id === watchedEmployeeId) || null);
    } else {
      setSelectedEmployee(null);
    }
  }, [watchedEmployeeId, employees]);

  useEffect(() => {
    if (watchedCarId) {
      setSelectedCar(cars.find(c => c.id === watchedCarId) || null);
    } else {
      setSelectedCar(null);
    }
  }, [watchedCarId, cars]);

  useEffect(() => {
    if (certificate) setDocumentCategory(certificate.carId ? 'car' : 'employee');
  }, [certificate]);

  const onFormSubmit = (data: CrewCertificateFormData) => {
    const expiryDate = new Date(data.expiryDate);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    let status: 'valid' | 'expired' | 'expiring_soon' = 'valid';
    if (expiryDate < today) {
      status = 'expired';
    } else if (expiryDate <= thirtyDaysFromNow) {
      status = 'expiring_soon';
    }

    const submitData = {
      ...data,
      status,
      documentCategory,
      employeeId: documentCategory === 'employee' ? data.employeeId : '',
      carId: documentCategory === 'car' ? data.carId : ''
    };
    onSubmit(submitData);
  };

  const employeeOptions = employees.map(employee => ({
    value: employee.documentId || employee.id,
    label: `${employee.name} (${employee.code})`
  }));

  const carOptions = cars.map(car => ({
    value: car.documentId || car.id,
    label: `${car.plateNumber} - ${car.ownerName}`
  }));

  // Document types are configured in Document Settings ('employee'/'crew'
  // categories apply to employee documents). The hardcoded lists remain as
  // a fallback when no types are configured or the role can't read them.
  const documentTypeOptions = useMemo(() => {
    const dbNames = (documentTypes as any[])
      .filter(dt => documentCategory === 'car' ? dt.category === 'car' : dt.category !== 'car')
      .map(dt => dt.name)
      .filter(Boolean);
    const names = dbNames.length
      ? dbNames
      : (documentCategory === 'employee' ? employeeDocumentTypes : carDocumentTypes);
    const current = certificate?.courseType;
    if (current && !names.includes(current)) names.unshift(current);
    return names.map(type => ({ value: type, label: type }));
  }, [documentTypes, documentCategory, certificate]);

  const professionOptions = useMemo(() => {
    if (ranksData && Array.isArray(ranksData) && ranksData.length > 0) {
      return ranksData.map((rank: any) => ({
        value: rank.name || rank.title || '',
        label: rank.name || rank.title || ''
      }));
    }
    return [];
  }, [ranksData]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {certificate ? 'Edit Document' : 'Add New Document'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <DocumentCategorySelector
            documentCategory={documentCategory}
            setDocumentCategory={setDocumentCategory}
          />

          <DocumentFormFields
            register={register as any}
            errors={errors as any}
            documentCategory={documentCategory}
            employeeOptions={employeeOptions}
            carOptions={carOptions}
            documentTypeOptions={documentTypeOptions}
            professionOptions={professionOptions}
            selectedEmployee={selectedEmployee}
            selectedCar={selectedCar}
          />

          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg mt-4">
            <DocumentFileUpload
              register={register as any}
              selectedFileName={selectedFileName}
              onFileChange={(e) => {
                const file = e.target.files?.[0];
                setSelectedFileName(file ? file.name : '');
              }}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : certificate ? 'Update Document' : 'Add Document'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrewAndCarsDocumentForm;
